import React, { Fragment } from "react";
import { styled } from '@mui/material/styles';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFacebookF, faTwitter } from "@fortawesome/free-brands-svg-icons";
import { faRss } from "@fortawesome/free-solid-svg-icons";
import "../../../App.css";
import femaLogo from "../../../assets/images/FEMA-logo.png";
import youtubeLogo from "../../../assets/images/youtube.png";

import handleGA, { gaEvents } from "../../googleAnalytics";

const PREFIX = 'Footer';

const classes = {
  root: `${PREFIX}-root`,
  top: `${PREFIX}-top`,
  primaryLinks: `${PREFIX}-primaryLinks`,
  secondaryLinks: `${PREFIX}-secondaryLinks`,
  agencyLogo: `${PREFIX}-agencyLogo`,
  bottomContainer: `${PREFIX}-bottomContainer`,
  bottom: `${PREFIX}-bottom`,
  contact: `${PREFIX}-contact`,
  contactRow: `${PREFIX}-contactRow`,
  social: `${PREFIX}-social`,
  socialIcon: `${PREFIX}-socialIcon`,
  contactTitle: `${PREFIX}-contactTitle`,
  contactLinks: `${PREFIX}-contactLinks`
};

const Root = styled('div')((
  {
    theme
  }
) => ({
  [`&.${classes.root}`]: {
    padding: 0,
    "& a": {
      color: "#000",
      textDecoration: "none",
    },
    "& a:hover": {
      color: "#1a4480",
    },
  },

  [`& .${classes.top}`]: {
    background: "#f0f0f0",
    padding: "2rem 0",
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      justifyContent: "center",
    },
  },

  [`& .${classes.primaryLinks}`]: {
    maxWidth: "64rem",
    margin: "auto",
    display: "flex",
    listStyle: "none",
    justifySelf: "start",
    padding: 0,
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
    "& li": {
      marginRight: "2em",
      "& a": {
        "&:hover": {
          color: "#005287",
        },
        [theme.breakpoints.down("sm")]: {
          lineHeight: "2",
        },
      },
    },
  },

  [`& .${classes.secondaryLinks}`]: {
    margin: "0 0.5rem",
  },

  [`& .${classes.agencyLogo}`]: {
    height: "6.5em",
    margin: "auto 0",
    alignItems: "center",
    display: "flex",
    [theme.breakpoints.down("sm")]: {
      height: "auto",
      width: "12em",
    },
  },

  [`& .${classes.bottomContainer}`]: {
    background: "#e6e6e6",
  },

  [`& .${classes.bottom}`]: {
    margin: "auto",
    maxWidth: "64rem",
    display: "grid",
    gridTemplateColumns: "auto auto",
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
  },

  [`& .${classes.contact}`]: {
    justifySelf: "right",
    display: "grid",
  },

  [`& .${classes.contactRow}`]: {
    display: "block",
    justifySelf: "end",
    margin: "0.5rem 0",
    alignSelf: "center",
  },

  [`& .${classes.social}`]: {
    listStyle: "none",
    display: "inline-flex",
    padding: 0,
    "& button": {
      margin: "0.3em",
      height: "4em",
      width: "4em",
      cursor: "pointer",
      "&:hover": {
        background: "#f3f3f3",
      },
    },
  },

  [`& .${classes.socialIcon}`]: {
    width: "1.5rem",
    fontSize: "1.5rem",
    margin: "auto",
  },

  [`& .${classes.contactTitle}`]: {
    fontWeight: "bold",
  },

  [`& .${classes.contactLinks}`]: {
    padding: 0,
    margin: 0,
    "& li": {
      listStyle: "none",
    },
  }
}));

const Footer = () => {

  const showTelAndEmail = false;

  const onClickExternalResource = (event, label) => {
    handleGA(gaEvents.NAVIGATE_TO_EXTERNAL_RESOURCE, label);
  };

  return (
    <Root className={classes.root}>
      <div className={classes.top}>
        <ul className={classes.primaryLinks}>
          <li>
            <a
              href="https://www.fema.gov/hazus"
              target="_blank"
              aria-label="vist FEMA's Hazus page"
              rel="noreferrer"
              onClick={(e) => onClickExternalResource(e, "fema.gov/hazus")}
            >
              Hazus
            </a>
          </li>
          {/* <li>
            <a
              href="https://www.fema.gov/hazus-quarterly-newsletter#"
              target="_blank"
              aria-label="vist Hazus Quarterly Newsletter"
            >
              Hazus Quarterly Newsletter
            </a>
          </li> */}
          <li>
            <a
              href="https://www.youtube.com/playlist?list=PL720Kw_OojlKuAIdn9cAFT1RqCQDE6Nye"
              target="_blank"
              aria-label="vist Hazus Youtube"
              rel="noreferrer"
              onClick={(e) => onClickExternalResource(e, "Hazus YouTube")}
            >
              Hazus YouTube
            </a>
          </li>
          <li>
            <a
              href="https://www.fema.gov/nri"
              target="_blank"
              aria-label="vist Hazus program's National Risk Index"
              rel="noreferrer"
              onClick={(e) => onClickExternalResource(e, "National Risk Index")}
            >
              National Risk Index
            </a>
          </li>
        </ul>
      </div>
      <div className={classes.bottomContainer}>
        <div className={classes.bottom}>
          <img
            className={classes.agencyLogo}
            src={femaLogo}
            alt="FEMA logo"
          ></img>
          <div className={classes.contact}>
            <div className={classes.contactRow}>
              <div className={classes.social}>
                <a
                  href="https://www.facebook.com/FEMA/"
                  aria-label="visit FEMA Facebook"
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => onClickExternalResource(e, "FEMA Facobook")}
                >
                  <button tabIndex={-1}>
                    <FontAwesomeIcon
                      className={classes.socialIcon}
                      icon={faFacebookF}
                      alt="Facebook"
                    />
                  </button>
                </a>
                <a
                  href="https://twitter.com/fema"
                  aria-label="visit FEMA Twitter"
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => onClickExternalResource(e, "FEMA Twitter")}
                >
                  <button tabIndex={-1}>
                    <FontAwesomeIcon
                      className={classes.socialIcon}
                      icon={faTwitter}
                      alt="Twitter"
                    />
                  </button>
                </a>
                <a
                  href="https://www.youtube.com/user/FEMA"
                  aria-label="visit FEMA YouTube"
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => onClickExternalResource(e, "FEMA YouTube")}
                >
                  <button tabIndex={-1}>
                    <img
                      className={classes.socialIcon}
                      src={youtubeLogo}
                      alt="YouTube"
                    ></img>
                  </button>
                </a>
                {/* <button>
                  <FontAwesomeIcon
                    className={classes.socialIcon}
                    icon={faRss}
                  />
                </button> */}
              </div>
            </div>
            {showTelAndEmail && (
              <Fragment>
                <div className={classes.contactRow}>
                  <span className={classes.contactTitle}>
                    Agency Contact Center
                  </span>
                </div>
                <div className={classes.contactRow}>
                  <ul className={classes.contactLinks}>
                    <li>
                      <a
                        href="tel:800-621-3362"
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) =>
                          onClickExternalResource(e, "FEMA phone")
                        }
                      >
                        (800) 621-3362
                      </a>
                    </li>
                    <li>
                      <a
                        href="https://www.fema.gov/"
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => onClickExternalResource(e, "fema.gov")}
                      >
                        fema.gov
                      </a>
                    </li>
                  </ul>
                </div>
              </Fragment>
            )}
          </div>
        </div>
      </div>
    </Root>
  );
};

export default Footer;
